"use client";

import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useEffect, useRef, useState } from "react";
import { ProductCard, type Product } from "@/components/chat/product-card";
import { OrderSummary } from "@/components/chat/order-summary";
import { CheckoutButton } from "@/components/chat/checkout-button";
import { AddressForm, type DeliveryAddress } from "@/components/chat/address-form";
import { Beef, MapPin, Search, FileText, Lock, ArrowLeft, Send } from "lucide-react";
import Link from "next/link";

export function ChatClient() {
  const { messages, sendMessage, status, error, addToolOutput, setMessages } = useChat({
    transport: new DefaultChatTransport({ api: "/api/chat" }),
  });

  const [historyLoaded, setHistoryLoaded] = useState(false);

  useEffect(() => {
    async function loadHistory() {
      try {
        const res = await fetch("/api/chat");
        if (res.ok) {
          const data = await res.json();
          if (data.messages && data.messages.length > 0) {
            setMessages(data.messages as UIMessage[]);
          }
        }
      } catch {
        // Silently fail — fresh chat is fine
      } finally {
        setHistoryLoaded(true);
      }
    }
    loadHistory();
  }, [setMessages]);

  function handleAddressSubmit(toolCallId: string, address: DeliveryAddress) {
    addToolOutput({
      tool: "collectAddress",
      toolCallId,
      output: address,
      state: "output-available",
    });
  }

  function handleAddressCancel(toolCallId: string) {
    addToolOutput({
      tool: "collectAddress",
      toolCallId,
      state: "output-error",
      errorText: "Customer cancelled address entry.",
    });
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] bg-[var(--color-muted)]">
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6 max-w-3xl mx-auto w-full">
        {!historyLoaded && (
          <div className="text-center py-12">
            <div className="flex gap-1 justify-center">
              <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            <p className="text-gray-400 text-sm mt-3">Loading conversation...</p>
          </div>
        )}

        {historyLoaded && messages.length === 0 && (
          <div className="text-center py-8">
            <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[var(--color-accent)] transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
            <Beef className="w-12 h-12 text-[var(--color-accent)] mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-xl font-heading font-bold mb-2">Welcome to The Butcher&apos;s Table</h2>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              Ask me about our products, place an order, or check your delivery. I can help you find halal cuts, mince, BBQ boxes, and more.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {["Show me halal lamb", "What beef mince do you have?", "I want a BBQ box", "Track my order"].map((s) => (
                <button
                  key={s}
                  onClick={() => sendMessage({ text: s })}
                  className="px-3 py-2 bg-white border border-[var(--color-border)] rounded-full text-sm text-gray-700 hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] md:max-w-[70%] ${
                message.role === "user"
                  ? "bg-[var(--color-accent)] text-white rounded-2xl rounded-tr-sm"
                  : "bg-white text-gray-800 rounded-2xl rounded-tl-sm border border-[var(--color-border)]"
              } px-4 py-3 shadow-sm`}
            >
              {message.parts.map((part, i) => {
                const key = `${message.id}-${i}`;

                switch (part.type) {
                  case "text":
                    return (
                      <div key={key} className="text-sm leading-relaxed prose-chat">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                            ul: ({ children }) => <ul className="list-disc pl-4 mb-1 space-y-0.5">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal pl-4 mb-1 space-y-0.5">{children}</ol>,
                            li: ({ children }) => <li>{children}</li>,
                            a: ({ href, children }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer" className="text-[var(--color-accent)] underline">
                                {children}
                              </a>
                            ),
                            table: ({ children }) => (
                              <div className="overflow-x-auto my-2 -mx-1 rounded-lg border border-gray-200">
                                <table className="min-w-full text-xs">{children}</table>
                              </div>
                            ),
                            thead: ({ children }) => (
                              <thead className="bg-gray-50">{children}</thead>
                            ),
                            tbody: ({ children }) => (
                              <tbody className="divide-y divide-gray-100">{children}</tbody>
                            ),
                            tr: ({ children }) => <tr>{children}</tr>,
                            th: ({ children }) => (
                              <th className="text-left font-semibold px-2.5 py-1.5 whitespace-nowrap border-b border-gray-200">
                                {children}
                              </th>
                            ),
                            td: ({ children }) => (
                              <td className="px-2.5 py-1.5 align-top">{children}</td>
                            ),
                          }}
                        >
                          {part.text}
                        </ReactMarkdown>
                      </div>
                    );

                  case "tool-collectAddress": {
                    if (part.state === "input-available" || part.state === "input-streaming") {
                      return (
                        <AddressForm
                          key={key}
                          onSubmit={(address) => handleAddressSubmit(part.toolCallId, address)}
                          onCancel={() => handleAddressCancel(part.toolCallId)}
                        />
                      );
                    }
                    if (part.state === "output-available") {
                      const addr = part.output as DeliveryAddress;
                      return (
                        <div key={key} className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/30 rounded-lg p-3 my-2 text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[var(--color-accent)]">✓</span>
                            <span className="font-medium text-[var(--color-accent)]">Delivery Address Confirmed</span>
                          </div>
                          <div className="text-gray-600">
                            {addr.name}<br />
                            {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />
                            {addr.city}, {addr.postcode}<br />
                            {addr.phone}
                          </div>
                        </div>
                      );
                    }
                    if (part.state === "output-error") {
                      return (
                        <div key={key} className="text-sm text-gray-400 py-2">
                          Address entry cancelled.
                        </div>
                      );
                    }
                    return null;
                  }

                  case "tool-checkDeliveryArea": {
                    const out = part.state === "output-available" ? part.output as {
                      delivers: boolean;
                      postcode: string;
                      zoneName?: string;
                      deliveryFee?: number;
                      freeDeliveryThreshold?: number | null;
                      coveredAreas?: Array<{ name: string; postcodes: string[] }>;
                    } : null;
                    if (out?.delivers) {
                      return (
                        <div key={key} className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/30 rounded-lg p-3 my-2 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-[var(--color-accent)]">✓</span>
                            <span className="font-medium text-[var(--color-accent)]">
                              We deliver to {out.postcode}
                            </span>
                          </div>
                          {out.deliveryFee !== undefined && (
                            <div className="text-gray-600 mt-1">
                              Delivery fee: £{out.deliveryFee.toFixed(2)}
                              {out.freeDeliveryThreshold && ` (free over £${out.freeDeliveryThreshold})`}
                            </div>
                          )}
                        </div>
                      );
                    }
                    if (out && !out.delivers) {
                      return (
                        <div key={key} className="bg-red-50 border border-red-200 rounded-lg p-3 my-2 text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-red-500">✕</span>
                            <span className="font-medium text-red-800">
                              We don&apos;t deliver to {out.postcode}
                            </span>
                          </div>
                          {out.coveredAreas && out.coveredAreas.length > 0 && (
                            <div className="text-gray-600">
                              We deliver to: {out.coveredAreas.map((a) => a.name).join(", ")}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div key={key} className="text-sm text-gray-400 py-2 flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" /> Checking delivery area...
                      </div>
                    );
                  }

                  case "tool-findProducts": {
                    const out = part.state === "output-available" ? part.output as { success: boolean; products?: Product[]; message?: string } : null;
                    if (out?.success) {
                      return <ProductCard key={key} products={out.products!} />;
                    }
                    if (out && !out.success) {
                      return (
                        <div key={key} className="text-sm text-gray-400 italic py-2">
                          No products found.
                        </div>
                      );
                    }
                    if (part.state === "output-error") {
                      return (
                        <div key={key} className="text-sm text-red-500 py-2">
                          Search failed. Please try again.
                        </div>
                      );
                    }
                    return (
                      <div key={key} className="text-sm text-gray-400 py-2 flex items-center gap-1.5">
                        <Search className="w-4 h-4" /> Searching products...
                      </div>
                    );
                  }

                  case "tool-createOrder": {
                    const out = part.state === "output-available" ? part.output as { success: boolean; orderId?: string; subtotal?: string; deliveryFee?: string; total?: string; currency?: string; error?: string } : null;
                    if (out?.success) {
                      return <OrderSummary key={key} order={out as any} />;
                    }
                    if (out && !out.success) {
                      return (
                        <div key={key} className="text-sm text-red-500 py-2">
                          {out.error || "Failed to create order."}
                        </div>
                      );
                    }
                    if (part.state === "output-error") {
                      return (
                        <div key={key} className="text-sm text-red-500 py-2">
                          Order creation failed.
                        </div>
                      );
                    }
                    return (
                      <div key={key} className="text-sm text-gray-400 py-2 flex items-center gap-1.5">
                        <FileText className="w-4 h-4" /> Creating your order...
                      </div>
                    );
                  }

                  case "tool-generateStripeCheckout": {
                    const out = part.state === "output-available" ? part.output as { success: boolean; checkoutUrl?: string; expiresInSeconds?: number; error?: string } : null;
                    if (out?.success) {
                      return (
                        <CheckoutButton
                          key={key}
                          url={out.checkoutUrl!}
                          expiresInSeconds={out.expiresInSeconds!}
                        />
                      );
                    }
                    if (out && !out.success) {
                      return (
                        <div key={key} className="text-sm text-red-500 py-2">
                          {out.error || "Failed to generate payment link."}
                        </div>
                      );
                    }
                    if (part.state === "output-error") {
                      return (
                        <div key={key} className="text-sm text-red-500 py-2">
                          Payment link generation failed.
                        </div>
                      );
                    }
                    return (
                      <div key={key} className="text-sm text-gray-400 py-2 flex items-center gap-1.5">
                        <Lock className="w-4 h-4" /> Generating secure payment link...
                      </div>
                    );
                  }

                  case "tool-lookupOrderStatus": {
                    const out = part.state === "output-available" ? part.output as { success: boolean; order?: { id: string; status: string; total: string } } : null;
                    if (out?.success && out.order) {
                      return (
                        <div key={key} className="bg-gray-50 rounded-lg p-3 my-2 text-sm">
                          <div className="font-medium">Order #{out.order.id.slice(0, 8)}</div>
                          <div>Status: <span className="capitalize">{out.order.status}</span></div>
                          <div>Total: £{out.order.total}</div>
                        </div>
                      );
                    }
                    return null;
                  }

                  case "tool-listMyOrders": {
                    const out = part.state === "output-available" ? part.output as { success: boolean; orders?: { id: string; status: string; total: string; createdAt: string }[] } : null;
                    if (out?.success && out.orders) {
                      return (
                        <div key={key} className="space-y-2 my-2">
                          {out.orders.map((o) => (
                            <div key={o.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                              <div className="font-medium">#{o.id.slice(0, 8)}</div>
                              <div>Status: <span className="capitalize">{o.status}</span> · £{o.total}</div>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }

                  case "tool-answerFAQ": {
                    const out = part.state === "output-available" ? part.output as { success: boolean; faqs?: { title: string; shortDescription: string }[] } : null;
                    if (out?.success && out.faqs) {
                      return (
                        <div key={key} className="space-y-2 my-2">
                          {out.faqs.map((f, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm">
                              <div className="font-medium">{f.title}</div>
                              {f.shortDescription && <div className="text-gray-600 mt-1">{f.shortDescription}</div>}
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }

                  default:
                    return null;
                }
              })}
            </div>
          </div>
        ))}

        {(status === "submitted" || status === "streaming") && (
          <div className="flex justify-start">
            <div className="bg-white border border-[var(--color-border)] rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-[var(--color-border)] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-[var(--color-border)] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-[var(--color-border)] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="text-center text-sm text-red-500 py-4">
            Something went wrong. Please try again.
          </div>
        )}
      </div>

      <ChatInput onSend={sendMessage} status={status} />
    </div>
  );
}

function ChatInput({
  onSend,
  status,
}: {
  onSend: (message: { text: string }) => void;
  status: string;
}) {
  const disabled = status !== "ready" && status !== "error";
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function autoGrow(el: HTMLTextAreaElement) {
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }

  function submit() {
    const text = value.trim();
    if (!text || disabled) return;
    onSend({ text });
    setValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  }

  const canSend = value.trim().length > 0 && !disabled;

  return (
    <div
      className="bg-white/95 backdrop-blur-sm border-t border-[var(--color-border)] px-3 sm:px-4 pt-3"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
    >
      <div className="max-w-3xl mx-auto w-full flex items-end gap-2 bg-[var(--color-muted)] rounded-3xl border border-[var(--color-border)] focus-within:border-[var(--color-accent)] transition-colors px-2 py-1.5">
        <textarea
          ref={textareaRef}
          name="message"
          rows={1}
          value={value}
          placeholder="Ask about products, place an order…"
          disabled={disabled}
          onChange={(e) => {
            setValue(e.target.value);
            autoGrow(e.target);
          }}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-transparent px-2.5 py-2 text-[15px] sm:text-sm focus:outline-none disabled:opacity-50 resize-none max-h-40 overflow-y-auto leading-relaxed"
          style={{ minHeight: "40px" }}
        />
        <button
          type="button"
          disabled={!canSend}
          onClick={submit}
          aria-label="Send message"
          className="shrink-0 bg-[var(--color-accent)] text-white rounded-full w-10 h-10 flex items-center justify-center transition-all hover:bg-[var(--color-accent-light)] disabled:bg-[var(--color-border)] disabled:text-gray-400 mb-0.5"
        >
          <Send className="w-[18px] h-[18px]" strokeWidth={2} />
        </button>
      </div>
      <p className="max-w-3xl mx-auto w-full text-center text-[11px] text-gray-400 mt-1.5 hidden sm:block">
        Shift + Enter for a new line
      </p>
    </div>
  );
}
